<template>
  <div class="min-h-screen  pt-24 pb-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-5xl mx-auto">
      <div class="text-center mb-12">
        <h1 class="font-heading text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-[0_0_15px_rgba(0,242,255,0.3)]">
          Get in touch
        </h1>
        <p v-if="data?.intro" class="text-text-muted text-lg max-w-2xl mx-auto whitespace-pre-line">
          {{ data.intro }}
        </p>
        <p v-else class="text-text-muted text-lg max-w-2xl mx-auto">
          Questions, partnerships, feedback — drop us a line.
        </p>
      </div>

      <div v-if="pending" class="flex justify-center py-20">
        <div class="w-10 h-10 border-3 border-white/10 border-t-primary rounded-full animate-spin"></div>
      </div>

      <div v-else class="grid md:grid-cols-5 gap-6">
        <!-- Contact info -->
        <div class="md:col-span-2 bg-[#15161a] border border-white/5 rounded-lg p-6 md:p-8 shadow-2xl backdrop-blur-sm relative overflow-hidden">
          <div class="absolute top-0 right-0 w-48 h-48 bg-primary/5 rounded-full blur-[100px] pointer-events-none"></div>
          <h2 class="font-heading text-sm font-bold text-text-muted uppercase tracking-widest mb-5">Direct channels</h2>

          <div v-if="hasAnyChannel" class="relative space-y-5">
            <div v-if="data.email">
              <p class="text-xs text-text-muted uppercase tracking-wider font-semibold mb-1">Email</p>
              <a :href="'mailto:' + data.email" class="text-white hover:text-primary transition-colors break-all">{{ data.email }}</a>
            </div>
            <div v-if="data.phone">
              <p class="text-xs text-text-muted uppercase tracking-wider font-semibold mb-1">Phone</p>
              <a :href="'tel:' + data.phone" class="text-white hover:text-primary transition-colors">{{ data.phone }}</a>
            </div>
            <div v-if="data.address">
              <p class="text-xs text-text-muted uppercase tracking-wider font-semibold mb-1">Address</p>
              <p class="text-white whitespace-pre-line">{{ data.address }}</p>
            </div>
            <div v-if="data.hours">
              <p class="text-xs text-text-muted uppercase tracking-wider font-semibold mb-1">Hours</p>
              <p class="text-white whitespace-pre-line">{{ data.hours }}</p>
            </div>
          </div>
          <div v-else class="relative text-text-muted text-sm">
            Contact information has not been configured yet.
          </div>

          <div class="mt-8 pt-6 border-t border-white/5 text-xs text-text-muted">
            Need quick answers? Browse the
            <NuxtLink :to="localePath('/faq')" class="text-primary hover:underline font-semibold">FAQ</NuxtLink>
            first — many questions are answered there.
          </div>
        </div>

        <!-- Contact form -->
        <div class="md:col-span-3">
          <div v-if="!data.enableContactForm" class="bg-[#15161a] border border-white/5 rounded-lg p-8 md:p-10 text-center text-text-muted">
            <p class="text-lg mb-2">Direct messaging isn't active right now.</p>
            <p class="text-sm">Please reach us via the channels on the left.</p>
          </div>

          <form
            v-else-if="!submitted"
            class="bg-[#15161a] border border-white/5 rounded-lg p-6 md:p-8 shadow-2xl backdrop-blur-sm relative overflow-hidden"
            @submit.prevent="submit"
          >
            <div class="absolute top-0 right-0 w-64 h-64 bg-accent/5 rounded-full blur-[100px] pointer-events-none"></div>

            <div class="relative space-y-4">
              <div class="grid md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs font-semibold text-text-muted mb-1.5">Name</label>
                  <el-input v-model="form.name" placeholder="Your name" :maxlength="200" />
                </div>
                <div>
                  <label class="block text-xs font-semibold text-text-muted mb-1.5">Email</label>
                  <el-input v-model="form.email" type="email" placeholder="you@example.com" :maxlength="200" />
                </div>
              </div>
              <div>
                <label class="block text-xs font-semibold text-text-muted mb-1.5">Subject <span class="text-text-dim font-normal">(optional)</span></label>
                <el-input v-model="form.subject" placeholder="What's this about?" :maxlength="200" />
              </div>
              <div>
                <label class="block text-xs font-semibold text-text-muted mb-1.5">Message</label>
                <el-input
                  v-model="form.message"
                  type="textarea"
                  :rows="6"
                  :maxlength="10000"
                  show-word-limit
                  placeholder="Tell us what you need…"
                />
              </div>

              <!-- Honeypot — hidden from users, bots will fill it. Positioned
                   off-screen rather than display:none so slightly smarter
                   bots that skip hidden fields also fall in. -->
              <div aria-hidden="true" class="absolute -left-[9999px] top-0 opacity-0 pointer-events-none">
                <label>Website (leave empty)</label>
                <input v-model="form.website" type="text" tabindex="-1" autocomplete="off" />
              </div>

              <div v-if="error" class="p-3 rounded-lg bg-red-500/5 border border-red-500/20 text-xs text-red-300">
                {{ error }}
              </div>

              <div class="flex items-center justify-end gap-3 pt-2">
                <button
                  type="submit"
                  :disabled="submitting || !canSubmit"
                  class="px-5 py-2.5 rounded-lg text-sm font-semibold transition-all bg-primary text-black hover:bg-primary/90 disabled:bg-white/5 disabled:text-text-muted disabled:cursor-not-allowed"
                >
                  {{ submitting ? 'Sending…' : 'Send message' }}
                </button>
              </div>
            </div>
          </form>

          <!-- Success state -->
          <div
            v-else
            class="bg-[#15161a] border border-emerald-500/20 rounded-lg p-8 md:p-10 text-center"
          >
            <div class="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <svg class="w-7 h-7 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <h2 class="text-xl font-semibold text-white mb-2">Thanks — message received.</h2>
            <p class="text-text-muted text-sm">We'll reply to <strong class="text-white">{{ sentEmail }}</strong> as soon as we can.</p>
            <button
              class="mt-6 px-4 py-2 rounded-lg text-sm font-semibold bg-white/5 text-white hover:bg-white/10 border border-white/10"
              @click="resetForm"
            >
              Send another
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed, ref } from 'vue'

definePageMeta({
  title: 'Contact',
})



const localePath = useLocalePath()
const { data, pending } = await useFetch('/api/public/contact')

const url = useRequestURL()
const host = url.hostname
if (host.startsWith('admin.')) {
  setPageLayout('admin')
} else if (host.startsWith('creator.')) {
  setPageLayout('creator')
}

const hasAnyChannel = computed(() =>
  !!(data.value?.email || data.value?.phone || data.value?.address || data.value?.hours)
)

const form = reactive({
  name: '',
  email: '',
  subject: '',
  message: '',
  website: ''        // honeypot
})
const submitting = ref(false)
const submitted = ref(false)
const sentEmail = ref('')
const error = ref('')

const canSubmit = computed(() =>
  form.name.trim().length >= 2
  && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
  && form.message.trim().length >= 10
)

async function submit() {
  if (!canSubmit.value || submitting.value) return
  submitting.value = true
  error.value = ''
  try {
    await $fetch('/api/public/contact', {
      method: 'POST',
      body: {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        website: form.website
      }
    })
    sentEmail.value = form.email.trim()
    submitted.value = true
  } catch (err) {
    error.value = err?.data?.statusMessage || err?.statusMessage || 'Could not send right now. Please try again.'
  } finally {
    submitting.value = false
  }
}

function resetForm() {
  form.name = ''
  form.email = ''
  form.subject = ''
  form.message = ''
  form.website = ''
  submitted.value = false
  sentEmail.value = ''
  error.value = ''
}
</script>
